import React, { useEffect, useState } from 'react';
import { DollarSign, AlertTriangle, XCircle, Package, Activity, TrendingUp } from 'lucide-react';
import inventoryManagementService from '../services/inventoryManagementService';

const InventoryDashboard = ({ coverage = 15, leadTime = 7 }) => {
    const [kpis, setKpis] = useState({
        totalInventoryValue: 0,
        outOfStockCount: 0,
        lowStockCount: 0,
        totalProducts: 0,
        inventoryAccuracy: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchKPIs = async () => {
            try {
                const data = await inventoryManagementService.getKPIs({ coverage, leadTime });
                setKpis(data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching KPIs:', err);
                setError('Error cargando indicadores');
                setLoading(false);
            }
        };

        fetchKPIs();
    }, [coverage, leadTime]);

    if (loading) return <div className="p-4 text-center">Cargando indicadores...</div>;
    if (error) return <div className="p-4 text-center text-red-500">{error}</div>;

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Lógica de color para Dinero Estancado
    const getStationaryMoneyColor = (amount) => {
        if (amount > 150000000) {
            return {
                bg: 'bg-red-100',
                text: 'text-red-600',
                icon: 'text-red-600',
                severity: 'CRÍTICO',
                message: '⚠️ Urgente: Mover inventario - Capital inmovilizado alto'
            };
        }
        if (amount > 75000000) {
            return {
                bg: 'bg-yellow-100',
                text: 'text-yellow-600',
                icon: 'text-yellow-600',
                severity: 'ADVERTENCIA',
                message: 'Atención: Acelerar rotación de inventario'
            };
        }
        return {
            bg: 'bg-green-100',
            text: 'text-green-600',
            icon: 'text-green-600',
            severity: 'SALUDABLE',
            message: 'Rotación de inventario saludable'
        };
    };

    // Lógica de color para Balance Neto
    const getNetBalanceColor = (netBalance) => {
        if (netBalance < 0) {
            return {
                bg: 'bg-red-100',
                text: 'text-red-600',
                icon: 'text-red-600',
                severity: 'CRÍTICO',
                message: '⚠️ Requiere inversión urgente en inventario'
            };
        }
        if (netBalance > 100000000) {
            return {
                bg: 'bg-yellow-100',
                text: 'text-yellow-600',
                icon: 'text-yellow-600',
                severity: 'ADVERTENCIA',
                message: '⚠️ Advertencia: Posible sobreinventario - Revisar'
            };
        }
        if (netBalance > 25000000) {
            return {
                bg: 'bg-green-100',
                text: 'text-green-600',
                icon: 'text-green-600',
                severity: 'SALUDABLE',
                message: 'Inventario equilibrado'
            };
        }
        return {
            bg: 'bg-emerald-100',
            text: 'text-emerald-600',
            icon: 'text-emerald-600',
            severity: 'EQUILIBRADO',
            message: 'Inventario casi equilibrado'
        };
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {/* Proyección de Ventas - Ambos valores */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                    <DollarSign size={24} />
                </div>
                <div>
                    <p className="text-sm text-gray-500 font-medium">Proyección de Ventas con Inventario Actual</p>
                    <p className="text-2xl font-bold text-gray-800">{formatCurrency(kpis.totalInventoryValueNoVat || 0)}</p>
                    <p className="text-xs text-blue-600 font-medium">Valor antes de IVA</p>
                    <p className="text-xs text-gray-500">Valor con IVA: {formatCurrency(kpis.totalInventoryValue || 0)}</p>
                </div>
            </div>

            {/* Costo de Mercancía de Inventario Actual */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center">
                <div className="p-3 rounded-full bg-indigo-100 text-indigo-600 mr-4">
                    <Activity size={24} />
                </div>
                <div>
                    <p className="text-sm text-gray-500 font-medium">Costo de Mercancía de Inventario Actual</p>
                    <p className="text-2xl font-bold text-gray-800">{formatCurrency(kpis.merchandiseCost || 0)}</p>
                    <p className="text-xs text-indigo-600 font-medium">Costo antes de IVA</p>
                    <p className="text-xs text-gray-500">
                        Costo con IVA: {formatCurrency(kpis.merchandiseCostWithIva || 0)} •
                        <span className="ml-1 font-bold text-amber-600 underline">
                            Rentabilidad Promedio: {(((1 - (kpis.costRatio || 0)) * 100)).toFixed(1)}%
                        </span>
                    </p>
                </div>
            </div>

            {/* Velocidad de Ventas Diaria */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center">
                <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                    <Activity size={24} />
                </div>
                <div>
                    <p className="text-sm text-gray-500 font-medium">Velocidad de Ventas Diaria</p>
                    <p className="text-2xl font-bold text-gray-800">{formatCurrency(kpis.dailySalesVelocityNet || 0)}</p>
                    <p className="text-xs text-purple-600 font-medium">Valor antes de IVA</p>
                    <p className="text-xs text-gray-500">Valor con IVA: {formatCurrency(kpis.dailySalesVelocity || 0)}</p>
                </div>
            </div>

            {/* Proyección Ventas Mes */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center">
                <div className="p-3 rounded-full bg-teal-100 text-teal-600 mr-4">
                    <DollarSign size={24} />
                </div>
                <div>
                    <p className="text-sm text-gray-500 font-medium">Proyección Ventas Mes</p>
                    <p className="text-2xl font-bold text-gray-800">{formatCurrency(kpis.monthlySalesProjectionNet || 0)}</p>
                    <p className="text-xs text-teal-600 font-medium">Valor antes de IVA</p>
                    <p className="text-xs text-gray-500">Valor con IVA: {formatCurrency(kpis.monthlySalesProjection || 0)}</p>
                </div>
            </div>


            {/* Resumen de Productos - Tarjeta Consolidada */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-gray-100 text-gray-600">
                        <Package size={20} />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">Resumen de Productos</p>
                </div>
                <div className="space-y-2">
                    {/* Total Productos */}
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <Package size={16} className="text-green-600" />
                            <span className="text-xs text-gray-600">Total Productos</span>
                        </div>
                        <span className="text-lg font-bold text-green-600">{kpis.totalProducts}</span>
                    </div>
                    {/* Stock Bajo */}
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={16} className="text-yellow-600" />
                            <span className="text-xs text-gray-600">Stock Bajo</span>
                        </div>
                        <span className="text-lg font-bold text-yellow-600">{kpis.lowStockCount}</span>
                    </div>
                    {/* Productos Agotados */}
                    <div className="flex items-center justify-between py-1.5">
                        <div className="flex items-center gap-2">
                            <XCircle size={16} className="text-red-600" />
                            <span className="text-xs text-gray-600">Agotados</span>
                        </div>
                        <span className="text-lg font-bold text-red-600">{kpis.outOfStockCount}</span>
                    </div>
                </div>
            </div>


            {/* Indicadores Financieros de Inventario - Tarjeta Consolidada */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-indigo-100 text-indigo-600">
                        <TrendingUp size={20} />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">Indicadores Financieros</p>
                </div>
                <div className="space-y-2">
                    {/* Dinero Estacionado */}
                    {(() => {
                        const stationaryMoney = kpis.totalStationaryMoney || 0;
                        const colorScheme = getStationaryMoneyColor(stationaryMoney);
                        return (
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                                <div className="flex items-center gap-2">
                                    <TrendingUp size={16} className={colorScheme.text} />
                                    <span className="text-xs text-gray-600">Dinero Estancado</span>
                                </div>
                                <span className={`text-lg font-bold ${colorScheme.text}`}>{formatCurrency(stationaryMoney)}</span>
                            </div>
                        );
                    })()}

                    {/* Déficit */}
                    <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={16} className="text-red-600" />
                            <span className="text-xs text-gray-600">Déficit (Faltante)</span>
                        </div>
                        <span className="text-lg font-bold text-red-600">{formatCurrency(kpis.totalDeficitMoney || 0)}</span>
                    </div>

                    {/* Balance Neto */}
                    {(() => {
                        const netBalance = (kpis.totalStationaryMoney || 0) - (kpis.totalDeficitMoney || 0);
                        const colorScheme = getNetBalanceColor(netBalance);
                        return (
                            <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                                <div className="flex items-center gap-2">
                                    <DollarSign size={16} className={colorScheme.text} />
                                    <span className="text-xs text-gray-600">Balance Neto</span>
                                </div>
                                <span className={`text-lg font-bold ${colorScheme.text}`}>{formatCurrency(netBalance)}</span>
                            </div>
                        );
                    })()}

                    {/* Días de Inventario */}
                    {kpis.inventoryDays !== null && kpis.inventoryDays !== undefined && (
                        <div className="flex items-center justify-between py-1.5">
                            <div className="flex items-center gap-2">
                                <Activity size={16} className={kpis.inventoryDays > 60 ? 'text-red-600' : kpis.inventoryDays > 30 ? 'text-yellow-600' : 'text-green-600'} />
                                <span className="text-xs text-gray-600">Días de Inventario</span>
                            </div>
                            <span className={`text-lg font-bold ${kpis.inventoryDays > 60 ? 'text-red-600' : kpis.inventoryDays > 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                                {kpis.inventoryDays} días
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InventoryDashboard;
